import { updateServiceWorkerSafely } from "@/lib/serviceWorker";

const chunkRetryKey = "geominer_chunk_retry_at";
const chunkRetryCooldownMs = 5 * 60 * 1000;
let recoveryInProgress = false;

export function isChunkLoadError(error: unknown) {
  const message = error instanceof Error
    ? error.message
    : error && typeof error === "object" && "message" in error && typeof (error as { message?: unknown }).message === "string"
      ? (error as { message: string }).message
      : String(error || "");
  return /failed to fetch dynamically imported module|loading (?:css )?chunk|chunkloaderror|importing a module script failed|cannot read properties of undefined \(reading ['"]default['"]\)/i.test(message);
}

export function reserveChunkReload(error: unknown, now = Date.now()) {
  if (!isChunkLoadError(error) || typeof window === "undefined") return false;

  try {
    const lastRetry = Number(window.sessionStorage.getItem(chunkRetryKey) || 0);
    if (now - lastRetry <= chunkRetryCooldownMs) return false;
    window.sessionStorage.setItem(chunkRetryKey, String(now));
    recoveryInProgress = true;
    return true;
  } catch {
    // A blocked storage API should not prevent one recovery attempt.
    recoveryInProgress = true;
    return true;
  }
}

export function isChunkRecoveryInProgress() {
  return recoveryInProgress;
}

export async function clearStaleAppCaches() {
  if (typeof window === "undefined") return;

  if ("caches" in window) {
    try {
      const cacheNames = await window.caches.keys();
      await Promise.all(
        cacheNames
          .filter((name) =>
            name.startsWith("workbox-precache") ||
            name.startsWith("geominer-pages") ||
            name.startsWith("geominer-assets")
          )
          .map((name) => window.caches.delete(name)),
      );
    } catch {
      // Reloading without cache cleanup still gives the browser a chance to recover.
    }
  }

  if ("serviceWorker" in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => updateServiceWorkerSafely(registration)));
    } catch {
      // Service-worker refresh is best-effort.
    }
  }
}
